import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Modal from '@components/modal/Modal'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import SimpleAlert from '@components/modal/SimpleAlert'
import ModalLoader from '@components/modal/ModalLoader'

import Layout from './AdminUserLayout'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

import api from '@api/api'

import useWorkflow from './useWorkflow'
import { SCREENS } from './workflow'

import { Langage, TypeUtilisateur } from '@api/enum'

import Logger from '@utils/logger'

interface Props {
  id?: string
  formStep?: AdminUserStep
  edit?: AdminUserStep
  societeId?: string
  manifestationId?: string
}

const init: Partial<AdminUserForm> = {
  nom: '',
  prenom: '',
  email: '',
  mobile: '',
  fonction: '',
  codeMagic: '',
  scopes: [],
  sites: [],
  manifestations: [],
  restrictions: [],
  langage: Langage.FR,
}

// Rules for edition
// no id -> new user
// id + edit -> user modification
// id + no edit -> user duplication
const AdminUserFormScreen = ({ id, formStep, edit, societeId, manifestationId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const users = useReducer(AdminStore.store, (s) => s.users)

  const [form, setForm] = React.useState(init as AdminUserForm)
  const [step, setStep] = React.useState(0)

  const [workflow] = useWorkflow(form, edit)

  React.useEffect(() => {
    if (id) {
      const user = users.find((u) => u.id === id)

      if (user) {
        setForm({
          nom: edit ? user.nom : '',
          prenom: edit ? user.prenom : '',
          email: edit ? user.email : '',
          mobile: edit ? user.numeroTelephonePortable : '',
          fonction: user.fonction,
          codeMagic: user.codeMagic,
          scopes: user.roleIds,
          sites: user.siteIds,
          manifestations: user.manifestationIds,
          societeId: user.societeId,
          typeUtilisateur: user.typeUtilisateur,
          restrictions: [],
          langage: user.langage || Langage.FR,
        })
      }
    }
  }, [id])

  React.useEffect(() => {
    Promise.all([api.societe.all(), api.manifestation.all(), api.roles.all()])
      .then(([socsRes, manifRes, roleRes]) => {
        AdminStore.actions.setServices(socsRes)
        AdminStore.actions.setManifestations(manifRes)
        AdminStore.actions.setRoles(roleRes)
      })
      .catch(Logger.error)
  }, [])

  React.useEffect(() => {
    if (societeId) {
      setForm((prevState) => ({
        ...prevState,
        societeId,
        typeUtilisateur: TypeUtilisateur.PRESTATAIRE,
      }))
    }
  }, [societeId])

  React.useEffect(() => {
    if (manifestationId) {
      setForm((prevState) => ({
        ...prevState,
        manifestations: [...prevState.manifestations, manifestationId],
        typeUtilisateur: TypeUtilisateur.ORGANISATEUR,
        restrictions: [...prevState.restrictions, 'manifestations'],
      }))
    }
  }, [manifestationId])

  React.useEffect(() => {
    const idx = workflow.findIndex((w) => w === formStep)

    if (idx > -1) {
      setStep(idx)
    }
  }, [formStep, workflow])

  const onForm = (item: Partial<AdminUserForm>) => {
    setForm({ ...form, ...item })
    return Promise.resolve()
  }

  const onNext = () => {
    if (step === workflow.length - 1) {
      onFinish()
    } else {
      const next = workflow[step + 1] || ''
      navigation.push(`/admin/users/form/${next}`)
    }
  }

  const onBack = () => {
    if (id && edit) {
      // no back for single change
      quit()
      return
    }

    if (step === 0) {
      navigation.goBack()
    } else {
      const previous = workflow[step - 1] || ''
      navigation.push(`/admin/users/form/${previous}`)
    }
  }

  const quit = () => {
    if (societeId) {
      navigation.jumpTo(`/admin/providers/${societeId}`)
    } else if (manifestationId) {
      navigation.jumpTo(`/admin/organizers/${manifestationId}`)
    } else if (id && edit) {
      navigation.jumpTo(`/admin/users/${id}`)
    } else {
      navigation.jumpTo(`/admin/users`)
    }
  }

  const finish = () => {
    Modal.close()
    quit()
  }

  const reset = () => {
    Modal.close()
    setStep(0)
    setForm(init as AdminUserForm)
  }

  const onFinish = () => {
    ModalLoader.open({ title: i18n.t('screens.admin.users.form.sending') })

    const body: CreationUserRequest = {
      nom: form.nom,
      prenom: form.prenom,
      email: form.email,
      numeroTelephonePortable: form.mobile,
      fonction: form.fonction,
      codeMagic: form.codeMagic,
      roleIds: form.scopes,
      siteIds: form.sites,
      manifestationIds: form.manifestations,
      langage: form.langage,
      typeUtilisateur: form.typeUtilisateur,
      societeId: form.societeId,
      enabled: true,
    }

    if (id && edit) {
      api.users
        .update(id, body)
        .then(() => {
          ModalLoader.close()
          finish()
        })
        .catch(() => {
          ModalLoader.close()

          SimpleAlert.open({
            title: i18n.t('common.error'),
            description: i18n.t('screens.incident.sendingFailed'),
            buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
          })
        })
    } else {
      api.users
        .create(body)
        .then(() => {
          ModalLoader.close()
          Modal.open({ Content: renderFinish, cancel: false })
        })
        .catch(() => {
          ModalLoader.close()

          SimpleAlert.open({
            title: i18n.t('common.error'),
            description: i18n.t('screens.admin.users.form.sendingFailed'),
            buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
          })
        })
    }
  }

  const renderFinish = () => {
    return (
      <FinishContainer>
        <Title>{i18n.t('screens.admin.users.form.sent')}</Title>
        <Info>{i18n.t('screens.admin.users.form.sentInfo')}</Info>
        <Buttons>
          <ButtonContainer>
            <Button label={i18n.t('screens.admin.users.form.title')} onClick={reset} color="light" status="active" />
          </ButtonContainer>
          <ButtonContainer>
            <Button label={i18n.t('common.finish')} onClick={finish} status="active" />
          </ButtonContainer>
        </Buttons>

        <Close activeOpacity={0.8} onPress={finish}>
          <Icon name="close" color={theme.colors.primary} size={25} />
        </Close>
      </FinishContainer>
    )
  }

  const screen = workflow[step]
  const FormScreen = SCREENS[screen]

  if (!screen) {
    return <Root left="close" onLeft={quit} header={i18n.t('screens.admin.users.form.title')} fixed loading />
  }

  return (
    <Root left="close" onLeft={quit} header={i18n.t('screens.admin.users.form.title')} fixed>
      {
        <ItemContainer key={screen}>
          <Layout step={screen} form={form} onBack={onBack} onNext={onNext} onQuit={quit}>
            <FormScreen form={form} onForm={onForm} onBack={onBack} />
          </Layout>
        </ItemContainer>
      }
    </Root>
  )
}

export default AdminUserFormScreen

const ItemContainer = styled(View)`
  flex: 1;
`

const FinishContainer = styled(View)`
  position: relative;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '24px 16px;' : '50px 140px')};
  max-width: 880px;
  background-color: ${(props) => props.theme.colors.background};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  right: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  padding: 15px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 24px;
  text-align: center;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const Buttons = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  margin-top: 38px;
`

const ButtonContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 270px;`}
  margin: 10px;
`
